<script>
import { MOLDURA_DO_THREAD } from '@modules/aawz-intelligence-v2/ui-registry/moldura-do-thread'
import RaciocinioLinha from '@modules/aawz-intelligence-v2/components/raciocinio-linha/raciocinio-linha.vue'
import PainelDeliberacao from '@modules/aawz-intelligence-v2/components/painel-deliberacao/painel-deliberacao.vue'
import NextSteps from '@modules/aawz-intelligence-v2/components/next-steps/next-steps.vue'
import OpenQuestions from '@modules/aawz-intelligence-v2/components/open-questions/open-questions.vue'
import PlanoProgress from '@modules/aawz-intelligence-v2/components/plano-progress/plano-progress.vue'
import { perguntasNaoCobertas } from '@modules/aawz-intelligence-v2/utils/perguntas-ao-gestor'

/**
 * Composição dos blocos da resposta do assistente, na ordem do protótipo:
 * raciocínio → deliberação → texto (slot do pai) → artifact → próximos passos
 * → perguntas em aberto.
 *
 * SÓ composição: nenhuma regra de negócio mora aqui. O que cada bloco mostra é
 * decidido por ele mesmo.
 *
 * ★ O bloco da deliberação é o `painel-deliberacao` do #1961, adotado no merge
 * com o `desenvolvimento` no lugar do nosso `agent-panel`: ele mostra
 * divergências entre áreas, o turno do orquestrador, os cards das cadeiras que
 * ainda não responderam, custo e sala ativa — nada disso o nosso tinha. Com ele
 * veio o shape da TRANSCRIÇÃO CRUA (`mesa`, `turnos`, `parecer`), não mais o
 * estado reduzido que o nosso redutor produzia.
 */
export default {
  name: 'IntelligenceV2AssistantBlocks',
  components: { RaciocinioLinha, PainelDeliberacao, NextSteps, OpenQuestions, PlanoProgress },
  props: {
    // Transcrição do painel (shape do `painel-deliberacao`), ou `null`.
    painel: {
      type: Object,
      default: null,
    },
    // `plano` do turno (spec-001 `preparacao-reuniao`, D8.2) — `{versao,
    // itens}` ou `null`. Só existe enquanto o atalho está executando (o
    // `apiChat` zera no fim do turno — o consultor já está lendo o documento
    // no painel lateral a partir daí).
    plano: {
      type: Object,
      default: null,
    },
    // Os passos do raciocínio, na ordem — racional da triage + um por evento
    // `thinking` (ao vivo) ou `message.thinking_steps` (persistida). É a lista
    // que a linha de raciocínio abre ao expandir.
    passos: {
      type: Array,
      default: function () {
        return []
      },
    },
    // O passo ATUAL do turno (`rotuloDoTurno`) — o texto com shimmer da linha
    // única enquanto o turno roda.
    passoAtual: {
      type: String,
      default: '',
    },
    // O passo atual é uma etapa de verdade (não o genérico "Pensando...") —
    // escolhe o modo do sinal da linha (órbita × pulso). Só repassado.
    passoDeTrabalho: {
      type: Boolean,
      default: false,
    },
    // Duração persistida do turno (`message.duracao_s`); `null` no histórico.
    duracaoSegundos: {
      type: Number,
      default: null,
    },
    // [T064] "Pensando por 1 min 12 s" — o fecho do turno, montado pela bolha
    // (`chat-message.passo.js`). Vazio em streaming e em turno curto; só
    // repassado.
    rotuloDoTempo: {
      type: String,
      default: '',
    },
    // O motivo do turno que morreu — a linha vira erro.
    erroDoTurno: {
      type: String,
      default: '',
    },
    // `{ [rótulo do passo]: 'texto' }` — o auxiliar de cada passo da lista
    // ("1.667 linhas"), calculado pela bolha; só repassado.
    auxiliares: {
      type: Object,
      default: function () {
        return {}
      },
    },
    streaming: {
      type: Boolean,
      default: false,
    },
    // Perguntas que o formulário do turno já faz (`perguntar_ao_gestor`). Não
    // servem para exibir — servem para SUMIR daqui: o Consultor lê o parecer do
    // painel e re-pergunta, como campo, a parte que o gestor consegue
    // responder, então sem filtrar as mesmas perguntas apareciam nos dois
    // blocos. O que sobra aqui é o que é das ÁREAS (enquadramento regulatório,
    // parecer de outra mesa) — pendência que o gestor vê mas não responde.
    perguntasJaNoFormulario: {
      type: Array,
      default: function () {
        return []
      },
    },
    // Gap G2: o backend não emite `artifact`. A prop existe para o dia em que
    // emitir (F22) — hoje sempre `[]` e nada renderiza. Sem mock, sem flag.
    artifacts: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  // A moldura do thread vem da constante compartilhada — ver
  // `ui-registry/moldura-do-thread.js`. Espalhada por `v-bind` para que
  // um atributo novo da moldura alcance os cinco sítios de uma vez.
  data() {
    return { MOLDURA_DO_THREAD }
  },
  computed: {
    // [11/09/2026] A linha de raciocínio é o ÚNICO indicador do turno: existe
    // desde o primeiro instante do streaming ("Pensando..."), fica como resumo
    // quando há passos persistidos, e vira erro quando o turno morreu. A
    // mensagem antiga sem `thinking` e sem erro não ganha linha nenhuma.
    // [spec-001 preparacao-reuniao] O Progress do `plano` é o único indicador
    // de trabalho ENQUANTO um atalho executa — "um indicador por vez" (§10 do
    // CLAUDE.md do módulo). Com plano ativo, a linha de raciocínio some: as
    // duas mostrariam a mesma coisa ("a IA está trabalhando") de formas
    // diferentes na mesma bolha.
    temPlano() {
      return Boolean(this.plano && Array.isArray(this.plano.itens) && this.plano.itens.length)
    },
    mostraPlano() {
      return this.temPlano
    },
    // ★ [gap Q-0741] "Um indicador por vez" vale ENQUANTO o turno roda. Na
    // bolha já fechada nenhum dos dois é indicador de trabalho: o Progress é o
    // registro do que foi feito (passos riscados) e a linha é o resumo com o
    // "Ns" do turno. Sem o `&& this.streaming`, toda resposta de atalho perdia
    // a duração e os passos do raciocínio assim que o plano passou a
    // sobreviver ao `end`.
    /**
     * ★ [T064] O turno que DUROU também mantém a linha, mesmo sem passo
     * nenhum registrado.
     *
     * Medido na tela em 17/09/2026 (`/aawz-intelligence/chat-ia`, pergunta
     * "Quantos clientes eu tenho?"): 20,5 s de turno cujo raciocínio veio
     * todo da máquina de FASE (`Raciocinando` → `Elaborando resposta`), sem
     * um `thinking_steps` sequer — no `end` a linha inteira sumia, e com ela
     * o "Pensando por 20 s" que a mensagem tinha para contar. O rótulo do
     * tempo é conteúdo: ele existe exatamente quando houve espera.
     *
     * Sem passos a linha não é expansível (`expansivel = temPassos`) — vira
     * uma linha estática com o tempo, que é o que o ChatGPT mostra.
     */
    mostraRaciocinio() {
      if (this.temPlano && this.streaming) return false
      if (this.streaming || this.erroDoTurno) return true
      if (this.rotuloDoTempo) return true
      return this.passos.length > 0
    },
    temPainel() {
      return Boolean(this.painel)
    },


    // No shape dele as duas listas moram no `parecer`, em snake_case — é o que
    // o backend manda, e o `painel-deliberacao` não as achata.
    parecer() {
      return (this.painel && this.painel.parecer) || null
    },
    proximosPassos() {
      return (this.parecer && this.parecer.proximos_passos) || []
    },
    perguntas() {
      const doPainel = (this.parecer && this.parecer.perguntas_em_aberto) || []
      return perguntasNaoCobertas(doPainel, this.perguntasJaNoFormulario)
    },
    // Só com passos de verdade. [11/09/2026] O card "Estruturando próximos
    // passos..." que aparecia enquanto o `encerramento` não chegava SAIU — era
    // um segundo indicador de trabalho no meio da resposta (medido pelo
    // usuário num turno real: "Raciocínio da IA" no topo, este no meio,
    // "Consolidando parecer" no rodapé). Quem diz "Consolidando parecer" é a
    // linha única do raciocínio; o bloco entra, animado, quando o parecer chega.
    mostraProximosPassos() {
      return this.proximosPassos.length > 0
    },
    mostraPerguntas() {
      return this.perguntas.length > 0
    },
  },
}
</script>

<template>
  <div class="assistant-blocks" v-bind="MOLDURA_DO_THREAD">
    <PlanoProgress v-if="mostraPlano" :itens="plano.itens" />

    <RaciocinioLinha
      v-if="mostraRaciocinio"
      :passos="passos"
      :passo-atual="passoAtual"
      :passo-de-trabalho="passoDeTrabalho"
      :streaming="streaming"
      :duracao-segundos="duracaoSegundos"
      :rotulo-do-tempo="rotuloDoTempo"
      :auxiliares="auxiliares"
      :erro="erroDoTurno"
    />

    <!-- `em-andamento` é dele (a linha "o consultor está escrevendo a partir
         daqui"); `recolher` é nosso: a mesa fecha quando a resposta chega,
         porque a partir daí ela virou andaime. -->
    <PainelDeliberacao
      v-if="temPainel"
      :painel="painel"
      :em-andamento="streaming"
      :recolher="!streaming"
    />

    <!-- [T101, spec-001 `pesquisa-internet-chat`] O que o turno FEZ antes de
         escrever: pesquisa na internet, código executado, arquivo gerado.

         ★ Fica ACIMA do texto de propósito, e é o único lugar da bolha em que
         isso vale. "A ordem do turno é UMA" (CLAUDE.md do módulo, 11/09/2026)
         tirou os cartões do streaming porque o texto crescia ACIMA deles e os
         empurrava a cada chunk — o reflow mora em quem está ABAIXO do texto
         que cresce. Acima dele, o bloco fica parado. É também o que a US-6
         pede ("dois cartões de fonte antes do texto começar") e o que sustenta
         a meta de ver a primeira fonte muito antes do primeiro parágrafo.

         ⚠️ E é ESTÁTICO, sem shimmer: "um indicador por vez" — quem anima
         enquanto o turno roda é a `RaciocinioLinha`, acima. -->
    <slot name="antes-do-texto" />

    <!-- Texto da resposta: o pai preenche, porque é ele que conhece a persona
         (HTML permitido, linkify, pós-render). -->
    <slot />

    <!-- Artifact (C19/C20) entra aqui em F22, quando o gap G2 fechar. -->

    <template v-if="temPainel">
      <NextSteps v-if="mostraProximosPassos" :passos="proximosPassos" />
      <OpenQuestions v-if="mostraPerguntas" :perguntas="perguntas" />
    </template>
  </div>
</template>
